import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight, Shield } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const crashServices = [
  "Panel beating and replacement",
  "Dent removal",
  "Frame straightening",
  "Paint matching and respraying",
  "Bumper repairs",
  "Glass replacement coordination",
  "Insurance claim assistance",
  "Free damage assessment"
];

const CrashRepairsPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    'Crash Repairs & Insurance Claims',
    'Professional crash repairs, collision repair, panel beating, and insurance claim assistance in Murray Bridge, South Australia. Free damage assessments available.',
    'https://www.jprcustoms.com.au/crash-repairs'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Crash Repairs & Insurance Claims"
        description="Professional crash repairs and collision repair in Murray Bridge SA. Panel beating, dent removal, frame straightening, insurance claims assistance. Free damage assessment. Call +61 417 119 544."
        keywords="crash repairs Murray Bridge, collision repair, panel beating, car accident repairs, insurance claims, dent removal, frame straightening, smash repairs"
        canonical="/crash-repairs"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1625047509248-ec889cbff17f?w=1920&q=80)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Crash Repairs &<br />
              <span className="text-lime-500">Insurance Claims</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Professional collision repair services with hassle-free insurance claim processing.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-lime-500 mb-6">Expert Crash Repair Services</h2>
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  Accidents happen, but getting your vehicle back to pre-accident condition doesn't have to be stressful. Our experienced team provides comprehensive crash repair services.
                </p>
                <p className={`mb-8 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  We work directly with all major insurance companies to streamline the claims process.
                </p>
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Our Crash Repair Services:</h3>
                <ul className="space-y-3">
                  {crashServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-6">
                <div className={`p-8 rounded-xl ${isDark ? 'bg-neutral-800' : 'bg-white shadow-lg'}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <Shield className="w-10 h-10 text-lime-500" />
                    <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-neutral-900'}`}>Insurance Claims Made Easy</h3>
                  </div>
                  <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>
                    We handle the paperwork and communicate directly with your insurance company.
                  </p>
                </div>
                <img 
                  src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?w=800&q=80" 
                  alt="Crash repair service"
                  className="w-full h-72 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Had an Accident?</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Get your free damage assessment today.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CrashRepairsPage;
